import React, { useEffect, useState } from "react";
import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Dashboard from "./Dashboard";
import Register from "./Auth/Register";
import Login from "./Auth/Login";
import theme from "./theme";
import { useAuth } from "./hooks/useAuth";
import CreateOrganization from "./components/CreateOrganization";
import ForgotPassword from "./Auth/ForgetPassword";
import ResetPassword from "./Auth/ResetPassword";
import GetStarted from "./Auth/GetStarted";
import "./index.css";
// import MouseParticles from "./components/MouseParticles";
import SuperAdmin from "./SuperAdmin/SuperAdmin";
import SADashboard from "./SuperAdmin/pages/Dashboard";
import SAOrganizations from "./SuperAdmin/pages/Organizations";
import NotFound from "./pages/NotFound";

// Custom Loading Component - Simplified for better performance
const ZenSightLoader: React.FC = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prev + Math.random() * 20;
      });
    }, 150);

    return () => clearInterval(timer);
  }, []);

  return (
  <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center overflow-hidden relative">
      {/* Background Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large Floating Circles (gold glow) */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-[#FBD28222] to-[#B5914322] rounded-full opacity-40 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-br from-[#B5914320] to-[#FBD28220] rounded-full opacity-35 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-32 w-40 h-40 bg-gradient-to-br from-[#FBD28218] to-[#B5914318] rounded-full opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Medium Geometric Shapes */}
        <div className="absolute top-1/4 left-1/3 w-16 h-16 bg-gradient-to-br from-[#FBD28226] to-[#B5914326] rounded-full opacity-40 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-2/3 right-1/4 w-20 h-20 bg-gradient-to-br from-[#B5914324] to-[#FBD28224] rounded-full opacity-35 animate-bounce" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Small Floating Dots */}
        <div className="absolute top-1/3 left-1/2 w-3 h-3 bg-[#FBD282] rounded-full animate-ping opacity-70"></div>
        <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-[#B59143] rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
        <div className="absolute bottom-1/3 left-1/4 w-4 h-4 bg-[#E8C16C] rounded-full animate-ping opacity-60" style={{ animationDelay: '0.7s' }}></div>
        <div className="absolute bottom-1/2 right-1/2 w-2 h-2 bg-[#FBD282] rounded-full animate-ping opacity-70" style={{ animationDelay: '1.2s' }}></div>
        
        {/* Diagonal Lines */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 left-10 w-32 h-0.5 bg-gradient-to-r from-transparent via-[#FBD28233] to-transparent transform rotate-45 opacity-40"></div>
          <div className="absolute top-20 right-20 w-24 h-0.5 bg-gradient-to-r from-transparent via-[#B5914333] to-transparent transform -rotate-45 opacity-35" style={{ animationDelay: '0.8s' }}></div>
          <div className="absolute bottom-20 left-20 w-28 h-0.5 bg-gradient-to-r from-transparent via-[#E8C16C33] to-transparent transform rotate-30 opacity-40" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(251, 210, 130, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(251, 210, 130, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="text-center relative z-10">
        {/* ZenSight Logo/Text */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-6xl font-bold mb-2">
            <span className="bg-clip-text text-transparent" style={{
              background: "linear-gradient(90deg, #FBD282, #B59143)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}>
              Zen
            </span>
            <span className="text-white">
              Sight
            </span>
          </h1>
          <p className="text-[#C3C9D3] text-lg animate-pulse">Loading your vision...</p>
        </div>

        {/* Progress Bar Container */}
        <div className="w-80 md:w-96 mx-auto mb-6">
          <div className="relative">
            {/* Background Bar */}
            <div className="w-full h-3 bg-[#1A1C1F] rounded-full overflow-hidden border border-[#2E2F33]">
              {/* Progress Bar */}
              <div 
                className="h-full bg-gradient-to-r from-[#FBD282] to-[#B59143] rounded-full transition-all duration-300 ease-out shadow-[0_0_12px_rgba(181,145,67,0.4)]"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Progress Percentage */}
        <div className="text-center">
          <span className="text-3xl font-bold text-white">
            {Math.round(Math.min(progress, 100))}%
          </span>
        </div>

        {/* Loading Dots */}
        <div className="flex justify-center space-x-2 mt-6">
          <div className="w-2 h-2 bg-[#FBD282] rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-[#B59143] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-[#E8C16C] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    </div>
  );
};

// Super Admin Route Guard Component
const SuperAdminRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => 
    localStorage.getItem("isSuperAdmin") === "true"
  );

  useEffect(() => {
    const checkSuperAdmin = () => {
      setIsSuperAdmin(localStorage.getItem("isSuperAdmin") === "true");
    };

    // Check immediately
    checkSuperAdmin();

    // Listen for storage changes (cross-tab)
    window.addEventListener("storage", checkSuperAdmin);

    // Listen for custom event (same-tab changes)
    window.addEventListener("superAdminStatusChanged", checkSuperAdmin);

    // Also check on focus (in case user switched tabs)
    window.addEventListener("focus", checkSuperAdmin);

    return () => {
      window.removeEventListener("storage", checkSuperAdmin);
      window.removeEventListener("superAdminStatusChanged", checkSuperAdmin);
      window.removeEventListener("focus", checkSuperAdmin);
    };
  }, []);

  if (!isSuperAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isSignedIn, loading, orgId } = useAuth();
  const [isDOMLoading, setIsDOMLoading] = useState(true);

  // DOM Loading effect - shows loader while components are being rendered
  useEffect(() => {
    if (!loading && isSignedIn !== undefined) {
      // Reduced delay for faster loading
      const timer = setTimeout(() => {
        setIsDOMLoading(false);
      }, 100); 

      return () => clearTimeout(timer);
    }
  }, [loading, isSignedIn]);

  // Show loader during auth loading OR DOM loading
  if (loading || isDOMLoading) {
    return <ZenSightLoader />;
  }

  const OrgId = orgId != null ? orgId : "Xyi09suiej75RuHayi";

  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      {/* <MouseParticles /> */}
      <Router>
        <Routes>
          <Route
            path="/admin"
            element={
              localStorage.getItem("isSuperAdmin") === "true" ? (
                <GetStarted />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="/superadmin"
            element={
              <SuperAdminRouteGuard>
                <SuperAdmin />
              </SuperAdminRouteGuard>
            }
          >
            <Route index element={<SADashboard />} />
            <Route path="organizations" element={<SAOrganizations />} />
          </Route>
          <Route
            path="/login"
            element={
              localStorage.getItem("isSuperAdmin") === "true" ? (
                <Navigate to="/superadmin" replace />
              ) : isSignedIn ? (
                <Navigate to={`/organization/${OrgId}`} replace />
              ) : (
                <Login />
              )
            } 
          />  
          <Route
            path="/register"
            element={
              localStorage.getItem("isSuperAdmin") === "true" ? (
                <Register />
              ) : (
                <NotFound />
              )
            }
          />
          <Route path="/create-organization" element={<CreateOrganization />} />
          <Route
            path="/organization/:organizationId/*"
            element={isSignedIn ? <Dashboard /> : <Navigate to="/login" />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ChakraProvider>
  );
};

export default App;
